import { JSX } from 'react'
import { Security } from '../../proto/generated/security'
import { SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { TradeOptions } from '../../reducer/scalper.reducer'
import { ViewportCandleInfo } from '../../util/chart'
import { DrawingEvents } from '../chartV2/drawingTools/drawing.interface'
import { MultiPaneItem } from '../multiPane/multiPaneLayout.interface'
import DashboardWrapper from './predefined/DashboardWrapper'
import { PredefinedDashboard, WidgetTypes } from './widgetDashboard.enums'


export type WidgetSettings<T> = {
    key: string,
    expressionMode: boolean,
    displayName?: string,
    securities: string[]
} & T

export type WidgetConfig<T> = {
    slotId: number,
    widgetType: WidgetTypes,
    settings: WidgetSettings<T>
}

export type WidgetDashboardConfig = {
    panes: MultiPaneItem,
    widgets: Record<number, WidgetConfig<any>>,
    activeWidget?: number,
    singleMode: boolean,
    syncSettings: DashboardSyncSettings
}


export type WidgetDashboardProps = {
    id: string,
    maxPanes?: number,
    defaultWidgetType?: WidgetTypes,
    allowInitialEmpty?: boolean,
    tradeOptions?: TradeOptions,
    disableSplit?: boolean
}

export type WidgetProps<T> = {
    dashboardId: string,
    slotId: number,
    allowSplit?: boolean,
    settings: WidgetConfig<T>['settings'],
    hoveredSlotId: number,
    tradeOptions?: TradeOptions
}

export interface WidgetDashboardContextType {
    registerWidget: () => void
}

export interface IWidget {
    onActive: () => void,
    onBlur: () => void,
    updateSettings: (settings: WidgetSettings<any>) => void,
    onRemove: () => void,
    onSyncSettingsChanged: (setting: keyof DashboardSyncSettings, value: boolean) => void,
    onSecurityUpdate?: (data: DashboardEventData[DashboardSyncEvents.SECURITY]) => void,
    onTimeBucketUpdate?: (data: DashboardEventData[DashboardSyncEvents.TIME_BUCKET]) => void,
    onCrosshairUpdate?: (data: DashboardEventData[DashboardSyncEvents.CROSSHAIR]) => void,
    onTimeUpdate?: (data: DashboardEventData[DashboardSyncEvents.TIME]) => void,
    onDateUpdate?: (data: DashboardEventData[DashboardSyncEvents.DATE]) => void,
    onDrawingUpdate?: (data: { eventType: DrawingEvents, data: any }) => void
}

export type DashboardSyncSettings = {
    security: boolean,
    timeBucket: boolean,
    crosshair: boolean,
    time: boolean,
    drawing: boolean
}

export enum DashboardSyncEvents {
    SECURITY = 'SECURITY',
    TIME_BUCKET = 'TIME_BUCKET',
    DATE = 'DATE',
    CROSSHAIR = 'CROSSHAIR',
    TIME = 'TIME',
    DRAWING = 'DRAWING'
}

export type DashboardEventData = {
    [DashboardSyncEvents.SECURITY]: {
        key: string,
        securities: string[],
        expressionMode: boolean
    },
    [DashboardSyncEvents.TIME_BUCKET]: SuperChartConfiguration_TimeBucket,
    [DashboardSyncEvents.CROSSHAIR]: [number, number], // [Time, Price]
    [DashboardSyncEvents.TIME]: ViewportCandleInfo & {
        timeBucket: SuperChartConfiguration_TimeBucket
        gotoDate: number | undefined
    },
    [DashboardSyncEvents.DRAWING]: {
        eventType: DrawingEvents,
        data: any
    },
    [DashboardSyncEvents.DATE]: number
}

export type PredefinedDashboardMenuType = {
    id: PredefinedDashboard,
    icon: JSX.Element,
    wrapper: DashboardWrapper,
    category: number
}


export interface IWidgetDashboard {
    splitPane: (paneId: number, numberOfDivisions: number, horizontal: boolean) => void
    deletePane: (paneId: number) => void,
    updateWidgetConfig: (slotId: number, settings: WidgetConfig<any>['settings']) => void,
    broadcast: <T extends DashboardSyncEvents>(id: number, event: T, data: DashboardEventData[T], forced?: boolean) => void,
    updateSecurity: (key: string, expressionMode: boolean, securities: Security[], displayName?: string) => void,
    getActiveWidget: () => WidgetConfig<any> | null
}